import { type FormEvent, useEffect, useMemo, useState } from "react";
import {
  getDemoSession,
  loginDemoAccount,
  logoutDemoAccount,
  registerDemoAccount,
  type DemoSession,
  validateDemoCredentials,
} from "../lib/demoAuth";

type Direction = "HIGHER" | "LOWER";

type DemoOrder = {
  id: string;
  source: string;
  symbol: string;
  period: string;
  amount: number;
  direction: Direction;
  createdAt: string;
};

const SIGNAL_PROVIDERS = [
  {
    id: "pulse",
    name: "Pulse 30s",
    tag: "短周期",
    winRate: "63.8%",
    trades: "1,284",
    drawdown: "12.6%",
    signal: { symbol: "BTC", period: "30s", direction: "HIGHER" as Direction },
  },
  {
    id: "vector",
    name: "Vector ETH",
    tag: "趋势",
    winRate: "61.4%",
    trades: "896",
    drawdown: "9.8%",
    signal: { symbol: "ETH", period: "5m", direction: "LOWER" as Direction },
  },
  {
    id: "aurum",
    name: "Aurum XAUT",
    tag: "黄金",
    winRate: "58.9%",
    trades: "427",
    drawdown: "8.2%",
    signal: { symbol: "XAUT", period: "15m", direction: "HIGHER" as Direction },
  },
] as const;

const ORDER_STORAGE_KEY = "bitevent-guide:demo-orders";

function loadOrders(): DemoOrder[] {
  try {
    const stored = localStorage.getItem(ORDER_STORAGE_KEY);
    return stored ? (JSON.parse(stored) as DemoOrder[]) : [];
  } catch {
    return [];
  }
}

function maskPhone(phone: string) {
  if (phone.length < 7) return phone;
  return `${phone.slice(0, 3)}••••${phone.slice(-3)}`;
}

export function MemberExperience() {
  const [session, setSession] = useState<DemoSession | null>(() => getDemoSession());
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<"register" | "login">("register");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [authPending, setAuthPending] = useState(false);
  const [orders, setOrders] = useState<DemoOrder[]>(loadOrders);
  const [symbol, setSymbol] = useState("BTC");
  const [period, setPeriod] = useState("30s");
  const [amount, setAmount] = useState("100");
  const [direction, setDirection] = useState<Direction>("HIGHER");
  const [selectedProvider, setSelectedProvider] = useState("pulse");
  const [copyActive, setCopyActive] = useState(false);
  const [toast, setToast] = useState("");

  const provider = useMemo(
    () => SIGNAL_PROVIDERS.find((item) => item.id === selectedProvider)!,
    [selectedProvider],
  );

  useEffect(() => {
    localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(orders));
  }, [orders]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2800);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!copyActive || !session) return;
    const timer = window.setInterval(() => {
      addOrder({
        source: `模拟跟单 · ${provider.name}`,
        symbol: provider.signal.symbol,
        period: provider.signal.period,
        direction: provider.signal.direction,
        amount: Math.max(1, Number(amount) || 100),
      });
      setToast(`已接收 ${provider.name} 的一条演示信号`);
    }, 30000);
    return () => window.clearInterval(timer);
  }, [amount, copyActive, provider, session]);

  function requireSession() {
    if (session) return true;
    setAuthMode("register");
    setAuthOpen(true);
    setToast("请先创建演示账户");
    return false;
  }

  function addOrder(order: Omit<DemoOrder, "id" | "createdAt">) {
    setOrders((current) => [
      {
        ...order,
        id: crypto.randomUUID(),
        createdAt: new Date().toISOString(),
      },
      ...current,
    ].slice(0, 12));
  }

  async function handleAuth(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const validation = validateDemoCredentials(phone, password);
    if (validation.phoneError || validation.passwordError) {
      setAuthError(validation.phoneError ?? validation.passwordError ?? "");
      return;
    }
    setAuthPending(true);
    setAuthError("");
    try {
      const nextSession =
        authMode === "register"
          ? await registerDemoAccount(phone, password)
          : await loginDemoAccount(phone, password);
      if (!nextSession) {
        setAuthError("手机号或密码不正确；若尚未创建账户，请先注册");
        return;
      }
      setSession(nextSession);
      setAuthOpen(false);
      setPassword("");
      setToast(authMode === "register" ? "演示账户创建成功" : "登录成功");
    } finally {
      setAuthPending(false);
    }
  }

  function handleManualOrder(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!requireSession()) return;
    const parsedAmount = Number(amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0 || parsedAmount > 10000) {
      setToast("模拟金额需在 1–10,000 USDT 之间");
      return;
    }
    addOrder({
      source: "手动模拟",
      symbol,
      period,
      direction,
      amount: parsedAmount,
    });
    setToast("模拟订单已加入持仓");
  }

  function toggleCopy() {
    if (!requireSession()) return;
    if (!copyActive) {
      addOrder({
        source: `模拟跟单 · ${provider.name}`,
        symbol: provider.signal.symbol,
        period: provider.signal.period,
        direction: provider.signal.direction,
        amount: Math.max(1, Number(amount) || 100),
      });
      setToast("模拟跟单已开启，并接收第一条演示信号");
    } else {
      setToast("模拟跟单已暂停");
    }
    setCopyActive((current) => !current);
  }

  return (
    <section className="section member-experience" id="experience">
      <div className="container">
        <div className="experience-heading reveal">
          <div>
            <p className="eyebrow">05 · 模拟交易与跟单中心</p>
            <h2>先用模拟资金，走完整个下单与跟单流程</h2>
            <p>
              所有账户、订单和信号只保存在当前浏览器，不连接 Bitevent，
              不读取第三方账户，也不会产生真实资金交易。
            </p>
          </div>
          <div className="account-chip">
            {session ? (
              <>
                <span className="account-avatar">{session.phone.slice(-2)}</span>
                <div><small>演示账户</small><strong>{maskPhone(session.phone)}</strong></div>
                <button
                  type="button"
                  onClick={() => {
                    logoutDemoAccount();
                    setSession(null);
                    setCopyActive(false);
                    setToast("已退出演示账户");
                  }}
                >
                  退出
                </button>
              </>
            ) : (
              <>
                <span className="account-avatar">?</span>
                <div><small>尚未登录</small><strong>创建本地演示账户</strong></div>
                <button type="button" onClick={() => setAuthOpen(true)}>注册 / 登录</button>
              </>
            )}
          </div>
        </div>

        <div className="experience-grid">
          <form className="demo-trade-card reveal" onSubmit={handleManualOrder}>
            <div className="demo-card-head">
              <div><span className="live-dot" /> 模拟下单</div>
              <small>DEMO ONLY</small>
            </div>
            <label>
              <span>交易品种</span>
              <select value={symbol} onChange={(event) => setSymbol(event.target.value)}>
                <option value="BTC">BTC / USDT</option>
                <option value="ETH">ETH / USDT</option>
                <option value="XAUT">XAUT / USDT</option>
              </select>
            </label>
            <label>
              <span>合约周期</span>
              <div className="demo-periods">
                {["30s", "1m", "5m", "15m"].map((value) => (
                  <button
                    className={period === value ? "active" : ""}
                    key={value}
                    type="button"
                    onClick={() => setPeriod(value)}
                  >
                    {value}
                  </button>
                ))}
              </div>
            </label>
            <label>
              <span>模拟金额</span>
              <div className="demo-amount">
                <input
                  type="number"
                  min="1"
                  max="10000"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                />
                <strong>USDT</strong>
              </div>
            </label>
            <div className="demo-directions">
              <button
                className={direction === "HIGHER" ? "higher active" : "higher"}
                type="button"
                onClick={() => setDirection("HIGHER")}
              >
                <span>↗ Higher</span><small>预测上涨</small>
              </button>
              <button
                className={direction === "LOWER" ? "lower active" : "lower"}
                type="button"
                onClick={() => setDirection("LOWER")}
              >
                <span>↘ Lower</span><small>预测下跌</small>
              </button>
            </div>
            <button className="demo-submit" type="submit">提交模拟订单</button>
            <p className="demo-fineprint">不会跳转官网，不会发送订单，不会扣除真实余额。</p>
          </form>

          <div className="copy-center reveal">
            <div className="demo-card-head">
              <div><span className={copyActive ? "live-dot" : "idle-dot"} /> 信号广场</div>
              <small>{copyActive ? "模拟运行中" : "未启动"}</small>
            </div>
            <div className="provider-list">
              {SIGNAL_PROVIDERS.map((item) => (
                <button
                  className={selectedProvider === item.id ? "provider active" : "provider"}
                  key={item.id}
                  type="button"
                  onClick={() => {
                    setSelectedProvider(item.id);
                    if (copyActive) setCopyActive(false);
                  }}
                >
                  <span className="provider-avatar">{item.name.slice(0, 1)}</span>
                  <div>
                    <strong>{item.name}</strong>
                    <small>{item.tag} · {item.trades} 笔演示记录</small>
                  </div>
                  <dl>
                    <div><dt>展示胜率</dt><dd>{item.winRate}</dd></div>
                    <div><dt>最大回撤</dt><dd>{item.drawdown}</dd></div>
                  </dl>
                </button>
              ))}
            </div>
            <div className="copy-settings">
              <div>
                <span>跟单模式</span>
                <strong>固定金额 · {Math.max(1, Number(amount) || 100)} USDT</strong>
              </div>
              <div>
                <span>下一条演示信号</span>
                <strong>{provider.signal.symbol} · {provider.signal.period} · {provider.signal.direction}</strong>
              </div>
            </div>
            <button
              className={copyActive ? "copy-button stop" : "copy-button"}
              type="button"
              onClick={toggleCopy}
            >
              {copyActive ? "暂停模拟跟单" : "开启模拟跟单"}
            </button>
            <div className="api-boundary">
              <span>官方 API 连接</span>
              <strong>未连接</strong>
              <p>正式版本仅应通过 Bitevent 明确授权的 API 接入，并在每次真实下单前提供用户确认与风控限制。</p>
            </div>
          </div>

          <div className="demo-orders reveal">
            <div className="demo-card-head">
              <div>模拟持仓</div>
              <small>{orders.length} 条</small>
            </div>
            {orders.length ? (
              <div className="order-table">
                {orders.map((order) => (
                  <div className="demo-order-row" key={order.id}>
                    <div>
                      <strong>{order.symbol} / USDT</strong>
                      <small>{order.source}</small>
                    </div>
                    <span className={order.direction === "HIGHER" ? "up" : "down"}>
                      {order.direction === "HIGHER" ? "↗ Higher" : "↘ Lower"}
                    </span>
                    <span>{order.period}</span>
                    <span>{order.amount.toFixed(2)} USDT</span>
                    <em>模拟待结算</em>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-orders">
                <span>◎</span>
                <p>还没有模拟订单</p>
                <small>手动下单或开启模拟跟单后，记录会显示在这里。</small>
              </div>
            )}
          </div>
        </div>
      </div>

      {authOpen && (
        <div className="auth-overlay" role="presentation" onMouseDown={() => setAuthOpen(false)}>
          <div
            className="auth-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="auth-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button className="auth-close" type="button" onClick={() => setAuthOpen(false)}>×</button>
            <span className="auth-logo">B</span>
            <p className="eyebrow">LOCAL DEMO ACCOUNT</p>
            <h3 id="auth-title">{authMode === "register" ? "创建演示账户" : "登录演示账户"}</h3>
            <p className="auth-intro">
              无验证码，仅用于当前浏览器内的功能演示。请勿使用真实平台密码。
            </p>
            <form onSubmit={handleAuth}>
              <label>
                <span>手机号</span>
                <input
                  autoComplete="tel"
                  placeholder="+66 81 234 5678"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                />
              </label>
              <label>
                <span>密码</span>
                <input
                  autoComplete={authMode === "register" ? "new-password" : "current-password"}
                  placeholder="至少 6 位"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </label>
              {authError && <p className="auth-error">{authError}</p>}
              <button className="auth-submit" disabled={authPending} type="submit">
                {authPending ? "处理中…" : authMode === "register" ? "注册并进入演示中心" : "登录"}
              </button>
            </form>
            <button
              className="auth-switch"
              type="button"
              onClick={() => {
                setAuthMode((mode) => mode === "register" ? "login" : "register");
                setAuthError("");
              }}
            >
              {authMode === "register" ? "已有演示账户？切换到登录" : "没有账户？创建一个演示账户"}
            </button>
            <div className="auth-security">
              密码使用随机盐值和 SHA-256 摘要保存在本机，不上传网络；正式账户系统仍需服务端密码哈希、限流、审计和找回机制。
            </div>
          </div>
        </div>
      )}

      {toast && <div className="demo-toast" role="status">{toast}</div>}
    </section>
  );
}
